import { useState } from "react";
import { useDebounce } from "react-use";
import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { SearchInput } from "components/SearchInput/SearchInput";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { SEARCH_DELAY_MS } from "constants/general";
import { IProject } from "interfaces/project";
import { formatID } from "core/formatID";
import { ProjectPercentIndicatorCircle } from "components/PercentIndicatorCircle/ProjectPercentIndicatorCircle";
import { DateWithTooltip } from "components/DateWithTooltip";

export interface ProjectsListProps {
  loadingInitialProjects: boolean;
  loadingMoreProjects: boolean;
  error: boolean;
  projects: IProject[];
  selectedProject: IProject | undefined;
  onChangeSearchValue(searchValue: string): void;
  onSelectProject(selectedProject: IProject): void;
  onScrolledToEnd: () => void;
}

export const ProjectsList = ({
  loadingInitialProjects,
  loadingMoreProjects,
  error,
  projects,
  selectedProject,
  onSelectProject,
  onChangeSearchValue,
  onScrolledToEnd
}: ProjectsListProps): JSX.Element => {
  const [searchInputValue, setSearchInputValue] = useState("");
  const SHOW_LIST_AVATARS = true;

  const [,] = useDebounce(
    () => onChangeSearchValue(searchInputValue),
    SEARCH_DELAY_MS,
    [searchInputValue]
  );

  const handleScroll = (event: React.UIEvent<HTMLElement, UIEvent>): void => {
    // if scrolled to the bottom of the container
    if (
      event.currentTarget.scrollTop ===
      event.currentTarget.scrollHeight - event.currentTarget.offsetHeight
    ) {
      onScrolledToEnd();
    }
  };

  return (
    <ProjectListWrapper>
      <SearchInput
        dataQa="PP-Search-Projects"
        value={searchInputValue}
        onChange={newSearchText => {
          setSearchInputValue(newSearchText);
        }}
        onClear={() => {
          setSearchInputValue("");
          onChangeSearchValue("");
        }}
      />

      {!!projects.length && (
        <>
          <List
            sx={{
              height: "100%",
              overflowY: "auto",
              py: 0,
              width: "100%"
            }}
            onScroll={handleScroll}
            data-qa="PP-Projects-Card-List"
          >
            {projects?.map(project => {
              return (
                <ListItem key={project.id} disablePadding divider>
                  <ListItemButton
                    onClick={() => onSelectProject(project)}
                    selected={
                      !!selectedProject && project.id === selectedProject?.id
                    }
                  >
                    {SHOW_LIST_AVATARS && (
                      <ListItemAvatar>
                        <ProjectPercentIndicatorCircle
                          // prettier-ignore
                          actionStates={{
                            open_action_count: project.open_action_count,
                            in_progress_action_count: project.in_progress_action_count,
                            in_review_action_count: project.in_review_action_count,
                            completed_action_count: project.completed_action_count,
                            canceled_action_count: project.canceled_action_count
                          }}
                          size={40}
                          strokeWidth={4}
                        />
                      </ListItemAvatar>
                    )}
                    <ListItemText
                      primary={
                        <ListItemContent>
                          <Box>
                            <Tooltip title={project.name}>
                              <Truncate>{project.name}</Truncate>
                            </Tooltip>
                            <Typography variant="body2" component="span">
                              {formatID(project.id, "P")}
                            </Typography>
                          </Box>
                          <Chip label={project.project_status} size="small" />
                        </ListItemContent>
                      }
                      secondary={
                        <div>
                          Due date: &nbsp;
                          {project?.target_completion_date ? (
                            <DateWithTooltip
                              date={project.target_completion_date}
                              showTime={false}
                            />
                          ) : (
                            <span>Not set</span>
                          )}
                        </div>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      <ProgressIndicatorContainer
        loading={loadingInitialProjects || loadingMoreProjects}
        error={error}
        errorMessage="An error occured whilst trying to load your projects"
      />

      {!loadingInitialProjects && !error && !projects.length && (
        <Typography sx={{ textAlign: "center" }}>
          There are no projects to display
        </Typography>
      )}
    </ProjectListWrapper>
  );
};

const ProjectListWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow: hidden;
`;
ProjectListWrapper.displayName = "ProjectListWrapper";

const Truncate = styled("span")`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
Truncate.displayName = "Truncate";

const ListItemContent = styled("div")`
  align-items: center;
  display: grid;
  grid-template-columns: minmax(0, 1fr) min-content;
  gap: ${({ theme }) => theme.spacing(1)};
`;
ListItemContent.displayName = "ListItemContent";
